<script setup lang="ts">
import { useControlId } from '../../composables/useControlId'
import { computed, nextTick, onBeforeUnmount, ref, shallowRef, watch } from 'vue'
import { UploadCloud, Plus, Trash2, FileText, Eye } from '@lucide/vue'
import { useBoundValue } from '../../composables/useBoundValue'
import { useFormControl } from '../../composables/useFormControl'
import { uploadRequest } from './request'
import { UPLOAD_LIST_IGNORE, type RcFile, type UploadFile, type UploadProps, type UploadRequestHandle } from './types'
export type { UploadFile } from './types'

defineOptions({ inheritAttrs: false })
const props = withDefaults(defineProps<UploadProps>(), { defaultFileList: () => [], name: 'file', method: 'POST', headers: () => ({}), data: () => ({}), withCredentials: false, accept: '', multiple: false, disabled: undefined, listType: 'text', maxCount: Infinity, drag: false, showUploadList: true })
const emit = defineEmits<{
  'update:fileList': [files: UploadFile[]]
  change: [info: { file: UploadFile; fileList: UploadFile[]; event?: { percent: number } }]
  preview: [file: UploadFile]
  reject: [file: RcFile, error: Error]
}>()
const { attrs, disabled, controlAttrs, onFieldChange } = useFormControl(props)
const innerList = shallowRef<UploadFile[]>(props.defaultFileList)
const boundList = useBoundValue(props, 'fileList', innerList)
const fileList = computed(() => boundList.value ?? [])
const fileInputRef = ref<HTMLInputElement | null>(null)
const isDragging = ref(false)
const removing = ref(new Set<string>())
const componentId = useControlId()
const requests = new Map<string, { handle?: UploadRequestHandle }>()
const previews = new Map<string, string>()
let sequence = 0
let mounted = true
const canSelect = computed(() => !disabled.value && (props.maxCount === 1 || fileList.value.length < props.maxCount))
function cancel(uid: string) { const request = requests.get(uid); requests.delete(uid); request?.handle?.abort() }
function releasePreview(uid: string) { const url = previews.get(uid); if (url) URL.revokeObjectURL(url); previews.delete(uid) }
watch(fileList, list => {
  const ids = new Set(list.map(file => file.uid))
  for (const uid of requests.keys()) if (!ids.has(uid)) cancel(uid)
  for (const uid of previews.keys()) if (!ids.has(uid)) releasePreview(uid)
}, { flush: 'sync' })
function notify(file: UploadFile, next: UploadFile[], event?: { percent: number }) {
  innerList.value = next
  emit('update:fileList', next)
  emit('change', { file, fileList: next, event })
  return onFieldChange()
}
async function updateRequest(uid: string, request: { handle?: UploadRequestHandle }, patch: Partial<UploadFile>, event?: { percent: number }) {
  await nextTick()
  if (!mounted || requests.get(uid) !== request) return
  const current = fileList.value.find(file => file.uid === uid)
  if (!current) { cancel(uid); return }
  if (patch.status === 'done' || patch.status === 'error') requests.delete(uid)
  const next = { ...current, ...patch }
  return notify(next, fileList.value.map(file => file.uid === uid ? next : file), event)
}
async function startRequest(file: UploadFile, raw: RcFile, payload: RcFile | Blob) {
  const request: { handle?: UploadRequestHandle } = {}
  requests.set(file.uid, request)
  try {
    const action = typeof props.action === 'function' ? await props.action(raw) : props.action ?? ''
    const data = typeof props.data === 'function' ? await props.data(raw) : props.data
    if (!mounted || requests.get(file.uid) !== request || !fileList.value.some(item => item.uid === file.uid)) return
    const options = {
      file: payload, filename: props.name, action, data, method: props.method, headers: props.headers, withCredentials: props.withCredentials,
      onSuccess: (response: unknown) => { void updateRequest(file.uid, request, { status: 'done', percent: 100, response }) },
      onError: (error: Error, response?: unknown) => { void updateRequest(file.uid, request, { status: 'error', error, response }) },
      onProgress: (event: { percent: number }) => { void updateRequest(file.uid, request, { percent: event.percent }, event) }
    }
    const transport = props.customRequest ?? uploadRequest
    request.handle = transport(options) || undefined
  } catch (error) {
    await updateRequest(file.uid, request, { status: 'error', error: error instanceof Error ? error : new Error(String(error)) })
  }
}
async function processFiles(selected: File[]) {
  if (!canSelect.value) return
  const files = (props.multiple ? selected : selected.slice(0, 1)).map(file => Object.assign(file, { uid: `${componentId}-${++sequence}` }) as RcFile)
  for (const raw of files) {
    if (!mounted || disabled.value) return
    let result: Awaited<ReturnType<NonNullable<UploadProps['beforeUpload']>>>
    let precheckError: Error | undefined
    try { result = await props.beforeUpload?.(raw, files) }
    catch (error) { result = false; precheckError = error instanceof Error ? error : new Error(String(error)); emit('reject', raw, precheckError) }
    if (!mounted || disabled.value) return
    if (result === UPLOAD_LIST_IGNORE) continue
    if (props.maxCount !== 1 && fileList.value.length >= props.maxCount) { emit('reject', raw, new Error(`最多选择 ${props.maxCount} 个文件`)); continue }
    const autoUpload = result !== false && Boolean(props.action || props.customRequest)
    const file: UploadFile = { uid: raw.uid, name: raw.name, size: raw.size, type: raw.type, originFileObj: raw, ...(autoUpload ? { status: 'uploading', percent: 0 } as const : {}), ...(precheckError ? { error: precheckError } : {}) }
    if (raw.type.startsWith('image/')) { file.thumbUrl = URL.createObjectURL(raw); previews.set(raw.uid, file.thumbUrl) }
    if (props.maxCount === 1) for (const existing of fileList.value) { cancel(existing.uid); releasePreview(existing.uid) }
    await notify(file, props.maxCount === 1 ? [file] : [...fileList.value, file])
    await nextTick()
    if (autoUpload && mounted && fileList.value.some(item => item.uid === file.uid)) void startRequest(file, raw, result instanceof Blob ? result : raw)
  }
}
function triggerSelect() { if (canSelect.value) fileInputRef.value?.click() }
async function handleFileSelect(event: Event) {
  const target = event.target as HTMLInputElement
  const files = Array.from(target.files ?? [])
  target.value = ''
  await processFiles(files)
}
async function handleRemove(file: UploadFile, event: MouseEvent) {
  event.stopPropagation()
  if (disabled.value || removing.value.has(file.uid)) return
  removing.value.add(file.uid)
  try {
    const allowed = await props.onRemove?.(file)
    if (allowed === false || !mounted || disabled.value) return
    cancel(file.uid)
    releasePreview(file.uid)
    await notify({ ...file, status: 'removed' }, fileList.value.filter(item => item.uid !== file.uid))
  } finally { removing.value.delete(file.uid) }
}
function handlePreview(file: UploadFile, event: MouseEvent) { event.stopPropagation(); emit('preview', file) }
function handleDragOver(event: DragEvent) { event.preventDefault(); if (canSelect.value) isDragging.value = true }
function handleDragLeave() { isDragging.value = false }
async function handleDrop(event: DragEvent) { event.preventDefault(); isDragging.value = false; if (event.dataTransfer) await processFiles(Array.from(event.dataTransfer.files)) }
onBeforeUnmount(() => { mounted = false; for (const uid of requests.keys()) cancel(uid); for (const uid of previews.keys()) releasePreview(uid) })
defineExpose({ abort: (file?: UploadFile) => { if (file) cancel(file.uid); else for (const uid of requests.keys()) cancel(uid) } })
</script>

<template>
  <div class="rr-upload" :class="[attrs.class, `rr-upload--${listType}`, { 'is-disabled': disabled }]" :style="attrs.style as any">
    <input
      ref="fileInputRef"
      v-bind="controlAttrs()"
      type="file"
      class="rr-upload__input"
      :accept="accept"
      :multiple="multiple"
      :disabled="disabled"
      @change="handleFileSelect"
    />

    <!-- Drag & Drop Zone -->
    <div
      v-if="drag"
      class="rr-upload__drag-zone"
      :class="{ 'is-dragging': isDragging }"
      role="button"
      :tabindex="canSelect ? 0 : -1"
      :aria-disabled="!canSelect"
      @keydown.enter.prevent="triggerSelect"
      @keydown.space.prevent="triggerSelect"
      @click="triggerSelect"
      @dragover="handleDragOver"
      @dragleave="handleDragLeave"
      @drop="handleDrop"
    >
      <UploadCloud :size="32" class="rr-upload__drag-icon" aria-hidden="true" />
      <p class="rr-upload__drag-text">点击或将文件拖拽到此区域上传</p>
      <slot name="dragHint">
        <span class="rr-upload__drag-hint">支持单个或批量上传</span>
      </slot>
    </div>

    <!-- Picture Card Trigger -->
    <div
      v-else-if="listType === 'picture-card' && canSelect"
      class="rr-upload__picture-card-btn"
      role="button"
      :tabindex="canSelect ? 0 : -1"
      :aria-disabled="!canSelect"
      @keydown.enter.prevent="triggerSelect"
      @keydown.space.prevent="triggerSelect"
      @click="triggerSelect"
    >
      <Plus :size="20" aria-hidden="true" />
      <span>上传图片</span>
    </div>

    <!-- Normal Button Trigger -->
    <div v-else-if="canSelect" class="rr-upload__trigger" @click="triggerSelect">
      <slot>
        <button type="button" class="secondary" :disabled="disabled">
          <UploadCloud :size="14" aria-hidden="true" />
          <span>选择文件</span>
        </button>
      </slot>
    </div>

    <!-- File List (Text or Picture Card) -->
    <div v-if="showUploadList && fileList.length > 0" class="rr-upload__list" :class="[`rr-upload__list--${listType}`]">
      <!-- Picture Card Item -->
      <template v-if="listType === 'picture-card'">
        <div
          v-for="file in fileList"
          :key="file.uid"
          class="rr-upload__card-item"
          :class="[`is-${file.status ?? 'selected'}`]"
        >
          <img v-if="file.thumbUrl || file.url" :src="file.thumbUrl || file.url" :alt="file.name" class="rr-upload__card-img" />
          <div v-else class="rr-upload__card-placeholder">
            <FileText :size="24" />
          </div>

          <div class="rr-upload__card-actions">
            <button
              v-if="file.url || file.thumbUrl"
              type="button"
              class="rr-upload__action-btn"
              title="预览"
              @click="handlePreview(file, $event)"
            >
              <Eye :size="16" />
            </button>
            <button
              type="button"
              class="rr-upload__action-btn"
              title="删除"
              :disabled="disabled || removing.has(file.uid)"
              @click="handleRemove(file, $event)"
            >
              <Trash2 :size="16" />
            </button>
          </div>
          <progress v-if="file.status === 'uploading'" :value="file.percent ?? 0" max="100" :aria-label="`${file.name} 上传进度`" />
          <span v-if="file.error" class="rr-upload__error" role="alert">{{ file.error.message }}</span>
        </div>
      </template>

      <!-- Text Item -->
      <template v-else>
        <div
          v-for="file in fileList"
          :key="file.uid"
          class="rr-upload__text-item"
          :class="[`is-${file.status ?? 'selected'}`]"
        >
          <FileText :size="14" class="rr-upload__file-icon" aria-hidden="true" />
          <span class="rr-upload__file-name" :title="file.name" @click="handlePreview(file, $event)">
            {{ file.name }}
          </span>
          <progress v-if="file.status === 'uploading'" :value="file.percent ?? 0" max="100" :aria-label="`${file.name} 上传进度`" />
          <span v-if="file.error" class="rr-upload__error" role="alert">{{ file.error.message }}</span>
          <button
            type="button"
            class="rr-upload__remove-btn"
            title="移除文件"
            :disabled="disabled || removing.has(file.uid)"
            @click="handleRemove(file, $event)"
          >
            <Trash2 :size="13" aria-hidden="true" />
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.rr-upload {
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-sizing: border-box;
}

.rr-upload__input {
  display: none;
}

.rr-upload__trigger {
  display: inline-flex;
}

.rr-upload__drag-zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  border: 1px dashed var(--rr-color-border-strong, #cbd2da);
  border-radius: var(--rr-radius-panel, 8px);
  background: var(--rr-color-surface-subtle, #f7f8fa);
  cursor: pointer;
  transition: all 150ms ease;
  text-align: center;
}

.rr-upload__drag-zone:hover,
.rr-upload__drag-zone.is-dragging {
  border-color: var(--rr-color-primary, #5a46e0);
  background: var(--rr-color-primary-soft, #f0edfc);
}

.rr-upload__drag-icon {
  color: var(--rr-color-primary, #5a46e0);
  margin-bottom: 8px;
}

.rr-upload__drag-text {
  margin: 0 0 4px;
  font-size: var(--rr-font-size-base, 13px);
  font-weight: 500;
  color: var(--rr-color-text-strong, #171a1f);
}

.rr-upload__drag-hint {
  font-size: var(--rr-font-size-sm, 12px);
  color: var(--rr-color-text-subtle, #8a94a2);
}

.rr-upload__picture-card-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 96px;
  height: 96px;
  border: 1px dashed var(--rr-color-border-strong, #cbd2da);
  border-radius: var(--rr-radius-panel, 8px);
  background: var(--rr-color-surface-subtle, #f7f8fa);
  color: var(--rr-color-text-muted, #67717f);
  font-size: var(--rr-font-size-xs, 11px);
  gap: 6px;
  cursor: pointer;
  transition: all 150ms ease;
}

.rr-upload__picture-card-btn:hover {
  border-color: var(--rr-color-primary, #5a46e0);
  color: var(--rr-color-primary, #5a46e0);
  background: var(--rr-color-primary-soft, #f0edfc);
}

.rr-upload__list--picture-card {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.rr-upload__card-item {
  position: relative;
  width: 96px;
  height: 96px;
  border: 1px solid var(--rr-color-border, #e1e5ea);
  border-radius: var(--rr-radius-panel, 8px);
  overflow: hidden;
  box-sizing: border-box;
}

.rr-upload__card-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.rr-upload__card-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: var(--rr-color-surface-subtle, #f7f8fa);
  color: var(--rr-color-text-subtle, #8a94a2);
}

.rr-upload__card-actions {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  opacity: 0;
  transition: opacity 150ms ease;
}

.rr-upload__card-item:hover .rr-upload__card-actions {
  opacity: 1;
}

.rr-upload__action-btn {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 4px !important;
  margin: 0 !important;
  border: 0 !important;
  background: transparent !important;
  color: #ffffff !important;
  cursor: pointer;
  min-height: unset !important;
  transition: transform 120ms ease;
}

.rr-upload__action-btn:hover {
  transform: scale(1.15);
}

.rr-upload__list--text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.rr-upload__text-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: var(--rr-radius-xs, 4px);
  background: var(--rr-color-surface-subtle, #f7f8fa);
  color: var(--rr-color-text, #2b3038);
  font-size: var(--rr-font-size-sm, 12px);
  transition: background 120ms ease;
}

.rr-upload__text-item:hover {
  background: var(--rr-color-canvas, #f3f5f7);
}

.rr-upload__file-icon {
  color: var(--rr-color-text-subtle, #8a94a2);
  flex-shrink: 0;
}

.rr-upload__file-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}

.rr-upload__file-name:hover {
  color: var(--rr-color-primary, #5a46e0);
}

.rr-upload__remove-btn {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 0 !important;
  margin: 0 !important;
  border: 0 !important;
  background: transparent !important;
  color: var(--rr-color-text-subtle, #8a94a2) !important;
  cursor: pointer;
  min-height: unset !important;
}

.rr-upload__remove-btn:hover {
  color: var(--rr-color-danger, #be3038) !important;
}

.rr-upload.is-disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.rr-upload__error { color: var(--rr-color-danger); overflow-wrap: anywhere; }
.rr-upload__card-item:focus-within .rr-upload__card-actions { opacity: 1; }
.rr-upload progress { max-width: 96px; accent-color: var(--rr-color-primary); }
@media (prefers-reduced-motion: reduce) { .rr-upload * { transition: none !important; } }
</style>
